// @flow
import _ from 'lodash';
import type { ThemeSettingAction, ThemeSettingState } from 'stores/flow-type/theme-settings.type';

import {
  UPDATE_FIELD,
  UPDATE_MULTI_FIELDS,
  CHANGE_ACTIVE_MENU_ITEM,
  CHANGE_SELECTED_PAGE,
  UPDATE_MENU_SETTING,
  REMOVE_ELEMENT,
  TOGGLE_HIDE_MENU_ITEM,
  UPDATE_WHOLE_MENU_SETTING,
  TOGGLE_EXPAND_FULLSCREEN,
  GET_THEME_SETTING_BY_ID_REQUEST,
  GET_THEME_SETTING_BY_ID_SUCCESS,
  GET_THEME_SETTING_BY_ID_FAILURE,
  GET_THEME_SETTING_DEFAULT_VALUE_BY_ID_REQUEST,
  GET_THEME_SETTING_DEFAULT_VALUE_BY_ID_SUCCESS,
  GET_THEME_SETTING_DEFAULT_VALUE_BY_ID_FAILURE,
  UPDATE_THEME_SETTING,
  EDIT_THEME_SETTING_BY_ID_REQUEST,
  EDIT_THEME_SETTING_BY_ID_SUCCESS,
  EDIT_THEME_SETTING_BY_ID_FAILURE,
  GET_CLONED_SOURCE_THEME_SETTING_BY_ID_REQUEST,
  GET_CLONED_SOURCE_THEME_SETTING_BY_ID_REQUEST_SUCCESS,
  GET_CLONED_SOURCE_THEME_SETTING_BY_ID_REQUEST_FAILURE,
  CHANGE_DEVICE,
  GET_LIST_THEME_DEFAULT_VALUE,
  GET_LIST_THEME_DEFAULT_VALUE_SUCCESS,
  GET_LIST_THEME_DEFAULT_VALUE_FAILURE,
  GET_THEME_SETTING_BY_NAME,
  GET_THEME_SETTING_BY_NAME_SUCCESS,
  GET_THEME_SETTING_BY_NAME_FAILURE
} from 'stores/flow-type/theme-settings.type';
import {
  MENU_SETTING_COLLECTION,
  MENU_SETTING_SEARCH,
  MENU_SETTING_COMMON
} from 'src/constants/theme-settings.jsx';
import { TYPE_UPDATE_SETTING, DEVICE, SIDEBAR_MENU } from 'src/constants/theme-settings';
import { flattenMenu, buildMenuFromThemeSetting } from 'src/helpers/theme-settings-helper';

const initialState: ThemeSettingState = {
  themeName: '',
  themePublished: false,
  activeMenuItem: '',
  activeMenuItemDetail: null,
  selectedPage: {},
  updateType: '', // Type of menu (collection | search | common)
  mainLayout: 'product-item-1',
  subLayout: '',
  collectionHeader: '',
  commonSetting: MENU_SETTING_COMMON,
  collectionSetting: MENU_SETTING_COLLECTION,
  searchSetting: MENU_SETTING_SEARCH,

  themeSetting: null, // Save for initialValues of Redux form
  themeSettingLoading: false,
  themeSettingSuccess: false,
  themeSettingError: undefined,

  themeSettingDefaultValue: null, // Default value of matching theme
  themeSettingDefaultValueOriginal: null,
  themeSettingDefaultThemeValue: null, // For default theme value (always have)
  themeSettingDefaultValueLoading: false,
  themeSettingDefaultValueSuccess: false,
  themeSettingDefaultValueError: undefined,

  editThemeSettingLoading: false,
  editThemeSettingSuccess: false,
  editThemeSettingError: undefined,

  isFormReplaceDirty: false, // Use for enable button save when re-initialize form
  firstTimeActivate: false,
  previewSetting: {},
  device: DEVICE.DESKTOP,
  labelTranslation: {},
  boostThemeLib: 'default',
  boostThemeLibVersion: 'latest',
  boostThemeLibName: 'default',

  clonedSourceThemeSetting: null,
  clonedSourceThemeSettingLoading: false,
  clonedSourceThemeSettingSuccess: false,
  clonedSourceThemeSettingError: null,

  sideBarMenu: SIDEBAR_MENU.SECTION,
  listThemeTemplate: [],
  listThemeTemplateLoading: false,
  listThemeTemplateSuccess: false,
  listThemeTemplateError: null,

  themeSettingByName: null,
  themeSettingByNameLoading: false,
  themeSettingByNameSuccess: false,
  themeSettingByNameError: null,

  themeLibTemplate: '',
  originThemeLibTemplate: '',
  menuLayoutModalId: 1
};

const themeSettingReducers = (
  state: ThemeSettingState = initialState,
  action: ThemeSettingAction
) => {
  const mapMenuByType = {
    [TYPE_UPDATE_SETTING.COLLECTION]: state.collectionSetting,
    [TYPE_UPDATE_SETTING.SEARCH]: state.searchSetting,
    [TYPE_UPDATE_SETTING.COMMON]: state.commonSetting
  };

  switch (action.type) {
    case UPDATE_FIELD: {
      const { fieldName, data } = action?.payload ?? {};
      return {
        ...state,
        [fieldName]: data
      };
    }

    case UPDATE_MULTI_FIELDS: {
      const { data } = action?.payload ?? {};
      return {
        ...state,
        ...data
      };
    }

    case CHANGE_ACTIVE_MENU_ITEM: {
      const { id, updateType } = action?.payload ?? {};
      let activeMenuItemDetail = null;

      let allItemsByType = flattenMenu(mapMenuByType[updateType]);
      for (let item of allItemsByType) {
        if (item.id === id || item.value === id) {
          activeMenuItemDetail = item;
          break;
        }
      }

      return {
        ...state,
        updateType,
        activeMenuItem: id,
        activeMenuItemDetail
      };
    }
    case CHANGE_SELECTED_PAGE: {
      const { payload } = action;
      return {
        ...state,
        selectedPage: payload
      };
    }
    case UPDATE_MENU_SETTING: {
      const { id, children, updateType } = action?.payload ?? {};
      const cloneState = _.cloneDeep(state);
      const mapMenuByType = {
        [TYPE_UPDATE_SETTING.COLLECTION]: cloneState.collectionSetting,
        [TYPE_UPDATE_SETTING.SEARCH]: cloneState.searchSetting,
        [TYPE_UPDATE_SETTING.COMMON]: cloneState.commonSetting
      };
      const menuItem = flattenMenu(mapMenuByType[updateType]).find(item => item.id === id);
      if (menuItem) {
        menuItem.children = children;
      }
      return {
        ...cloneState
      };
    }

    case UPDATE_WHOLE_MENU_SETTING: {
      const { children, updateType } = action?.payload ?? {};
      const cloneState = _.cloneDeep(state);
      const mapMenuByType = {
        [TYPE_UPDATE_SETTING.COLLECTION]: 'collectionSetting',
        [TYPE_UPDATE_SETTING.SEARCH]: 'searchSetting',
        [TYPE_UPDATE_SETTING.COMMON]: 'commonSetting'
      };

      return {
        ...cloneState,
        [mapMenuByType[updateType]]: children
      };
    }

    case REMOVE_ELEMENT: {
      const { activeMenuItem: activeMenuItemState, updateType: updateState } = state;
      const activeMenuItem = _.get(action, 'payload.activeMenuItem') || activeMenuItemState;
      const updateType = _.get(action, 'payload.updateType') || updateState;
      const cloneState = _.cloneDeep(state);
      const mapMenuByType = {
        [TYPE_UPDATE_SETTING.COLLECTION]: cloneState.collectionSetting,
        [TYPE_UPDATE_SETTING.SEARCH]: cloneState.searchSetting,
        [TYPE_UPDATE_SETTING.COMMON]: cloneState.commonSetting
      };

      let menuItem = null;
      menuItem = flattenMenu(mapMenuByType[updateType]).find(
        item => !!(item?.children || []).find(child => child.value === activeMenuItem)
      );
      if (menuItem) {
        menuItem.children = (menuItem?.children || []).filter(
          child => child.value !== activeMenuItem
        );
      }
      return {
        ...cloneState,
        activeMenuItem: '',
        activeMenuItemDetail: null,
        updateType: ''
      };
    }
    case TOGGLE_HIDE_MENU_ITEM: {
      const { id, updateType } = action?.payload ?? {};
      const cloneState = _.cloneDeep(state);
      const mapMenuByType = {
        [TYPE_UPDATE_SETTING.COLLECTION]: cloneState.collectionSetting,
        [TYPE_UPDATE_SETTING.SEARCH]: cloneState.searchSetting,
        [TYPE_UPDATE_SETTING.COMMON]: cloneState.commonSetting
      };
      let menuItem = null;
      menuItem = mapMenuByType[updateType].find(item => item.id === id);
      if (menuItem) {
        menuItem.isHidden = !menuItem.isHidden;
      }
      return {
        ...cloneState
      };
    }

    case TOGGLE_EXPAND_FULLSCREEN: {
      const { payload } = action;
      return {
        ...state,
        isExpandFullscreen: payload
      };
    }

    case GET_THEME_SETTING_BY_ID_REQUEST:
      return {
        ...state,
        themeSetting: null,
        themeSettingLoading: true,
        themeSettingSuccess: false,
        themeSettingError: undefined
      };
    case GET_THEME_SETTING_BY_ID_SUCCESS: {
      const { response, i18n, themeId } = action.payload;
      const reduxFormData = response?.data?.value || {};
      const MenuCollection = buildMenuFromThemeSetting(
        MENU_SETTING_COLLECTION,
        reduxFormData,
        i18n
      );
      const MenuSearch = buildMenuFromThemeSetting(MENU_SETTING_SEARCH, reduxFormData, i18n);
      const MenuCommon = buildMenuFromThemeSetting(MENU_SETTING_COMMON, reduxFormData, i18n);

      const objectLabelTranslation = (response?.labelTranslation?.data || []).reduce(
        (cur, next) => {
          return {
            ...cur,
            [next.key]: next
          };
        },
        {}
      );

      return {
        ...state,
        themeId,
        themeName: response?.themeName || '',
        themePublished: response?.isPublished || false,
        themeSetting: reduxFormData,
        themeSettingLoading: false,
        themeSettingSuccess: true,
        themeSettingError: undefined,
        collectionSetting: MenuCollection,
        searchSetting: MenuSearch,
        commonSetting: MenuCommon,
        firstTimeActivate: response?.data?.firstTimeActivate || false,
        previewSetting: response?.previewSetting || {},
        labelTranslation: objectLabelTranslation,
        boostThemeLib: response?.boostThemeLib,
        boostThemeLibVersion: response?.boostThemeLibVersion,
        boostThemeLibName: response?.boostThemeLib,
        themeLibTemplate: _.get(response, 'data.themeLibTemplate', ''),
        originThemeLibTemplate: _.get(response, 'data.themeLibTemplate', '')
      };
    }
    case GET_THEME_SETTING_BY_ID_FAILURE:
      return {
        ...state,
        themeSetting: null,
        themeSettingLoading: false,
        themeSettingSuccess: false,
        themeSettingError: action.payload
      };
    case UPDATE_THEME_SETTING: {
      const { setting, i18n } = action.payload;
      const MenuCollection = buildMenuFromThemeSetting(MENU_SETTING_COLLECTION, setting, i18n);
      const MenuSearch = buildMenuFromThemeSetting(MENU_SETTING_SEARCH, setting, i18n);
      const MenuCommon = buildMenuFromThemeSetting(MENU_SETTING_COMMON, setting, i18n);
      return {
        ...state,
        themeSetting: setting,
        themeSettingLoading: false,
        themeSettingSuccess: false,
        themeSettingError: undefined,
        collectionSetting: MenuCollection,
        searchSetting: MenuSearch,
        commonSetting: MenuCommon
      };
    }

    // Get theme setting default value
    case GET_THEME_SETTING_DEFAULT_VALUE_BY_ID_REQUEST:
      return {
        ...state,
        themeSettingDefaultValue: null,
        themeSettingDefaultThemeValue: null,
        themeSettingDefaultValueLoading: true,
        themeSettingDefaultValueSuccess: false,
        themeSettingDefaultValueError: undefined
      };
    case GET_THEME_SETTING_DEFAULT_VALUE_BY_ID_SUCCESS: {
      const { data: response } = action.payload;
      const { themeLibTemplate } = state;
      const themeDefaultValueArr = response?.data || [];

      // Replace other theme default value by theme lib template
      const defaultValue = themeDefaultValueArr.find(theme => {
        return themeLibTemplate
          ? theme.themeName === themeLibTemplate
          : theme.themeName !== 'default';
      });
      const defaultValueOriginal = themeDefaultValueArr.find(theme => {
        return theme.themeName !== themeLibTemplate && theme.themeName !== 'default';
      });
      const defaultThemeValue = themeDefaultValueArr.find(theme => theme.themeName === 'default');

      return {
        ...state,
        themeSettingDefaultValue: defaultValue
          ? {
              value: defaultValue?.value || {},
              valueByMenu: defaultValue?.valueByMenu || {},
              presetCollectionHeader: defaultValue?.presetCollectionHeader || {},
              presetSubLayout: defaultValue?.presetSubLayout || {}
            }
          : null,
        themeSettingDefaultThemeValue: {
          value: defaultThemeValue?.value || {},
          valueByMenu: defaultThemeValue?.valueByMenu || {},
          presetCollectionHeader: defaultThemeValue?.presetCollectionHeader || {},
          presetSubLayout: defaultThemeValue?.presetSubLayout || {}
        },
        themeSettingDefaultValueOriginal: {
          value: defaultValueOriginal?.value || {},
          valueByMenu: defaultValueOriginal?.valueByMenu || {},
          presetCollectionHeader: defaultValueOriginal?.presetCollectionHeader || {},
          presetSubLayout: defaultValueOriginal?.presetSubLayout || {}
        },
        themeSettingDefaultValueLoading: false,
        themeSettingDefaultValueSuccess: true,
        themeSettingDefaultValueError: undefined
      };
    }
    case GET_THEME_SETTING_DEFAULT_VALUE_BY_ID_FAILURE:
      return {
        ...state,
        themeSettingDefaultValue: null,
        themeSettingDefaultThemeValue: null,
        themeSettingDefaultValueLoading: false,
        themeSettingDefaultValueSuccess: false,
        themeSettingDefaultValueError: action.payload
      };

    // Edit theme setting
    case EDIT_THEME_SETTING_BY_ID_REQUEST:
      return {
        ...state,
        editThemeSettingLoading: true,
        editThemeSettingSuccess: false,
        editThemeSettingError: undefined
      };
    case EDIT_THEME_SETTING_BY_ID_SUCCESS: {
      const { data } = action.payload;
      const reduxFormData = data?.value || {};

      return {
        ...state,
        themeSetting: reduxFormData,
        editThemeSettingLoading: false,
        editThemeSettingSuccess: true,
        editThemeSettingError: undefined
      };
    }
    case EDIT_THEME_SETTING_BY_ID_FAILURE:
      return {
        ...state,
        editThemeSettingLoading: false,
        editThemeSettingSuccess: false,
        editThemeSettingError: action.payload
      };

    case GET_CLONED_SOURCE_THEME_SETTING_BY_ID_REQUEST:
      return {
        ...state,
        clonedSourceThemeSetting: null,
        clonedSourceThemeSettingLoading: true,
        clonedSourceThemeSettingSuccess: false,
        clonedSourceThemeSettingError: null
      };
    case GET_CLONED_SOURCE_THEME_SETTING_BY_ID_REQUEST_SUCCESS: {
      const { response } = action.payload;
      const reduxFormData = response?.data?.value || {};

      return {
        ...state,
        clonedSourceThemeSetting: reduxFormData,
        clonedSourceThemeSettingLoading: false,
        clonedSourceThemeSettingSuccess: true,
        clonedSourceThemeSettingError: null
      };
    }
    case GET_CLONED_SOURCE_THEME_SETTING_BY_ID_REQUEST_FAILURE:
      return {
        ...state,
        clonedSourceThemeSetting: null,
        clonedSourceThemeSettingLoading: false,
        clonedSourceThemeSettingSuccess: false,
        clonedSourceThemeSettingError: action.payload
      };

    case CHANGE_DEVICE:
      return { ...state, device: action.payload };

    case GET_LIST_THEME_DEFAULT_VALUE:
      return {
        ...state,
        listThemeTemplate: [],
        listThemeTemplateLoading: true,
        listThemeTemplateSuccess: false,
        listThemeTemplateError: null
      };
    case GET_LIST_THEME_DEFAULT_VALUE_SUCCESS: {
      const { response } = action.payload;
      const result = response?.data || {};

      return {
        ...state,
        listThemeTemplate: result,
        listThemeTemplateLoading: false,
        listThemeTemplateSuccess: true,
        listThemeTemplateError: null
      };
    }
    case GET_LIST_THEME_DEFAULT_VALUE_FAILURE:
      return {
        ...state,
        listThemeTemplate: [],
        listThemeTemplateLoading: false,
        listThemeTemplateSuccess: false,
        listThemeTemplateError: action.payload
      };

    case GET_THEME_SETTING_BY_NAME:
      return {
        ...state,
        themeSettingByName: null,
        themeSettingByNameLoading: true,
        themeSettingByNameSuccess: false,
        themeSettingByNameError: null
      };
    case GET_THEME_SETTING_BY_NAME_SUCCESS: {
      const { response } = action.payload;
      const result = response?.data || {};

      return {
        ...state,
        themeSettingByName: result,
        themeSettingByNameLoading: false,
        themeSettingByNameSuccess: true,
        themeSettingByNameError: null,
        themeSettingDefaultValue: !_.isEmpty(result)
          ? {
              value: result?.value || {},
              valueByMenu: result?.valueByMenu || {},
              presetCollectionHeader: result?.presetCollectionHeader || {},
              presetSubLayout: result?.presetSubLayout || {}
            }
          : null
      };
    }
    case GET_THEME_SETTING_BY_NAME_FAILURE:
      return {
        ...state,
        themeSettingByName: null,
        themeSettingByNameLoading: false,
        themeSettingByNameSuccess: false,
        themeSettingByNameError: action.payload
      };

    default:
      return state;
  }
};

export default themeSettingReducers;
